import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { GqlMenuSection, MenuSectionType } from 'types/graphql';
import { ProductCarousel, ProductCarouselV2 } from './product-carousel';
import { OfferCarousel } from './offer-carousel';

type CarouselsProps = {
  menuSections: GqlMenuSection[];
};

type ProductCarouselProps = {
  menuSection: GqlMenuSection;
  isProductCardsV2Enabled?: boolean;
};

function OfferSection({ menuSection }: Pick<ProductCarouselProps, 'menuSection'>): JSX.Element {
  return <OfferCarousel menuSection={menuSection} />;
}

function CustomSection({ menuSection, isProductCardsV2Enabled }: ProductCarouselProps): JSX.Element {
  const productsFilter = useMemo(() => {
    if (!menuSection.products) {
      throw new Error('CustomSection is missing products');
    }

    return {
      sortBy: `popularSortIdx`,
      productIds: menuSection.products,
    };
  }, [menuSection.products]);

  return isProductCardsV2Enabled ? (
    <ProductCarouselV2 menuSection={menuSection} productsFilter={productsFilter} />
  ) : (
    <ProductCarousel menuSection={menuSection} productsFilter={productsFilter} />
  );
}

const Image = styled.img`
  padding: 20px;
  width: 100%;
  height: auto;
`;

function ImageSection({ menuSection }: Omit<ProductCarouselProps, 'isProductCardsV2Enabled'>): JSX.Element {
  return <Image src={menuSection.image?.image ?? ''} alt={menuSection.image?.alt ?? ''} />;
}

function TopSellersSection({ menuSection, isProductCardsV2Enabled }: ProductCarouselProps): JSX.Element {
  const productsFilter = { sortBy: `popular` };

  return isProductCardsV2Enabled ? (
    <ProductCarouselV2 menuSection={menuSection} productsFilter={productsFilter} />
  ) : (
    <ProductCarousel menuSection={menuSection} productsFilter={productsFilter} />
  );
}

function RecentlyViewedSection({ menuSection, isProductCardsV2Enabled }: ProductCarouselProps): JSX.Element {
  const productsFilter = useMemo(() => {
    if (!menuSection.products) {
      throw new Error('RecentlyViewedSection is missing products');
    }

    return {
      sortBy: `popularSortIdx`,
      productIds: menuSection.products,
    };
  }, [menuSection.products]);

  return isProductCardsV2Enabled ? (
    <ProductCarouselV2 menuSection={menuSection} productsFilter={productsFilter} />
  ) : (
    <ProductCarousel menuSection={menuSection} productsFilter={productsFilter} />
  );
}

function StaffPicksSection({ menuSection, isProductCardsV2Enabled }: ProductCarouselProps): JSX.Element {
  const productsFilter = {
    sortBy: `popularSortIdx`,
    staffPicks: true,
  };

  return isProductCardsV2Enabled ? (
    <ProductCarouselV2 menuSection={menuSection} productsFilter={productsFilter} />
  ) : (
    <ProductCarousel menuSection={menuSection} productsFilter={productsFilter} />
  );
}

function SpecialsCarousel({ menuSection, isProductCardsV2Enabled }: ProductCarouselProps): JSX.Element {
  const productsFilter = { isOnSpecial: true };
  return isProductCardsV2Enabled ? (
    <ProductCarouselV2 menuSection={menuSection} productsFilter={productsFilter} />
  ) : (
    <ProductCarousel menuSection={menuSection} productsFilter={productsFilter} />
  );
}

function SubCategoryCarousel({ menuSection, isProductCardsV2Enabled }: ProductCarouselProps): JSX.Element {
  const productsFilter = useMemo(() => {
    if (!menuSection.subcategory) {
      throw new Error('SubCategoryCarousel is missing subcategory');
    }

    return {
      sortBy: `popularSortIdx`,
      subcategories: [menuSection.subcategory],
    };
  }, [menuSection.subcategory]);

  return isProductCardsV2Enabled ? (
    <ProductCarouselV2 menuSection={menuSection} productsFilter={productsFilter} />
  ) : (
    <ProductCarousel menuSection={menuSection} productsFilter={productsFilter} />
  );
}

function BrandCarousel({ menuSection, isProductCardsV2Enabled }: ProductCarouselProps): JSX.Element {
  const productsFilter = useMemo(() => {
    if (!menuSection.brandId) {
      throw new Error('BrandCarousel is missing brand');
    }

    return {
      sortBy: `popularSortIdx`,
      brandIds: [menuSection.brandId],
    };
  }, [menuSection.brandId]);

  return isProductCardsV2Enabled ? (
    <ProductCarouselV2 menuSection={menuSection} productsFilter={productsFilter} />
  ) : (
    <ProductCarousel menuSection={menuSection} productsFilter={productsFilter} />
  );
}

function CategoryCarousel({ menuSection, isProductCardsV2Enabled }: ProductCarouselProps): JSX.Element {
  const productsFilter = useMemo(() => {
    if (!menuSection.category) {
      throw new Error('CategoryCarousel is missing category');
    }

    return {
      sortBy: `popularSortIdx`,
      types: [menuSection.category],
    };
  }, [menuSection.category]);

  return isProductCardsV2Enabled ? (
    <ProductCarouselV2 menuSection={menuSection} productsFilter={productsFilter} />
  ) : (
    <ProductCarousel menuSection={menuSection} productsFilter={productsFilter} />
  );
}

function CollectionCarousel({ menuSection, isProductCardsV2Enabled }: ProductCarouselProps): JSX.Element {
  const { sortBy, collectionId } = menuSection;
  const productsFilter = { sortBy, collectionId };

  return isProductCardsV2Enabled ? (
    <ProductCarouselV2 menuSection={menuSection} productsFilter={productsFilter} />
  ) : (
    <ProductCarousel menuSection={menuSection} productsFilter={productsFilter} />
  );
}

const sectionTypeToCarousel: Record<MenuSectionType, React.FC<ProductCarouselProps>> = {
  [MenuSectionType.category]: CategoryCarousel,
  [MenuSectionType.brand]: BrandCarousel,
  [MenuSectionType.recentlyViewed]: RecentlyViewedSection,
  [MenuSectionType.subcategory]: SubCategoryCarousel,
  [MenuSectionType.specials]: SpecialsCarousel,
  [MenuSectionType.staffPicks]: StaffPicksSection,
  [MenuSectionType.topSellers]: TopSellersSection,
  [MenuSectionType.custom]: CustomSection,
  [MenuSectionType.offers]: OfferSection,
  [MenuSectionType.collection]: CollectionCarousel,
  [MenuSectionType.image]: ImageSection,
};

export function Carousels({ menuSections }: CarouselsProps): JSX.Element | null {
  const flags = useFlags();
  const isProductCardsV2Enabled = flags['growth.ecomm.admin-product-card-v2.rollout'] ?? false;

  return (
    <>
      {menuSections.map((section) => {
        const CarouselComponent = sectionTypeToCarousel[section.sectionType];
        return (
          <CarouselComponent menuSection={section} key={section.id} isProductCardsV2Enabled={isProductCardsV2Enabled} />
        );
      })}
    </>
  );
}
